import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { compose, graphql } from 'react-apollo';

import { withUser } from '../contexts/user';
import { updateProfileMutation } from 'apps/ask/mutations/update-profile';

import Button from 'shared/Button';
import UserAvatar from 'apps/ask/components/UserAvatar';
import UserAccountTypeSwitch from 'apps/ask/components/UserAccountTypeSwitch';

import SegmentAnalytics from '../../../helpers/SegmentAnalytics.js';

class Profile extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      success: false,
      error: '',
      currentPassword: '',
      newPassword: '',
      isSubmitting: false,
      ...this.resolveState(props)
    };
  }

  handleInputChange = (name) => (event) => {
    this.setState({
      [name]: event.target.value
    });
  }

  handleSubmit = async (event) => {
    if (event) { event.preventDefault(); }

    const { isSubmitting, email, accountType, name, newPassword, currentPassword } = this.state;
    const { updateProfileMutation, user } = this.props;
    const accountTypeChanged = user.currentUser.accountType !== accountType;
    const standardError = 'An unexpected error occured';

    if (isSubmitting) return;

    try {
      this.setState({ isSubmitting: true, success: false, error: '' });

      const response = await updateProfileMutation({
        variables: {
          input: {
            email,
            name,
            accountType,
            newPassword,
            currentPassword
          }
        }
      });
      const result = get(response, 'data.updateProfile');

      this.setState({ isSubmitting: false });

      if (result.success) {
        this.setState({
          error: '',
          success: true,
          newPassword: '',
          currentPassword: '',
          ...this.resolveStateFromUser(result.user)
        });
        user.logIn(result.user); // updates user profile
        if (accountTypeChanged) {
          SegmentAnalytics.track('User account type changed', {
            accountType
          });
        }
      } else {
        this.setState({ error: result.notice || standardError });
        SegmentAnalytics.track('User Profile error', {
          error: result.notice
        });
      }
    } catch (error) {
      this.setState({ error: standardError, isSubmitting: false });
    }
  }

  resolveState(props) {
    if (!props.user.currentUser) return {};

    return {
      ...this.resolveStateFromUser(props.user.currentUser)
    };
  }

  resolveStateFromUser(user) {
    return {
      user,
      name: user.name,
      email: user.email,
      accountType: user.accountType
    };
  }

  render() {
    const {
      error,
      success,
      isSubmitting,
      user,
      name,
      accountType,
      email,
      newPassword,
      currentPassword
    } = this.state;

    const changed = newPassword || user.email !== email || user.name !== name || user.accountType !== accountType;
    const saveDisabled = isSubmitting || !changed;
    const confirmWithPassword = newPassword || email !== (user && user.email);

    return (
      <div className="profile-view">
        <div className="with-sidebar-layout">
          <div className="sidebar">
            <UserAvatar size="big" />
          </div>

          <div className="content">
            <form onSubmit={this.handleSubmit} noValidate>
              {error && (
                <span className="form-message error">{ error }</span>
              )}

              {success && (
                <span className="form-message success">
                  Your profile has been updated.
                </span>
              )}

              <div className="form-row name-account-type">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    name="name"
                    type="text"
                    placeholder="Type your name"
                    value={name}
                    onChange={this.handleInputChange('name')}
                  />
                </div>

                <div className="form-group account-type">
                  <label>Account Type</label>

                  <UserAccountTypeSwitch value={accountType} onChange={(value) => { this.setState({ accountType: value }); }} />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  name="email"
                  type="email"
                  placeholder="Type your email"
                  value={email}
                  onChange={this.handleInputChange('email')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input
                  name="new-password"
                  type="password"
                  placeholder="Leave blank if you don't want to change it"
                  value={newPassword}
                  onChange={this.handleInputChange('newPassword')}
                />
              </div>

              {confirmWithPassword && (
                <div className="form-group">
                  <label htmlFor="password">Your current password</label>
                  <input
                    name="password"
                    type="password"
                    placeholder="Type your current password"
                    value={currentPassword}
                    onChange={this.handleInputChange('currentPassword')}
                  />
                  <span className="hint">
                    For security reasons, you must provide your current password when you change your email or password.
                  </span>
                </div>
              )}

              <div className="actions">
                <Button
                  type="submit"
                  className="button button--medium button--gradient no-anim"
                  showSpinner={isSubmitting}
                  disabled={saveDisabled}
                >
                  Save Updates
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

Profile.propTypes = {
  updateProfileMutation: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired
};

export default compose(
  graphql(updateProfileMutation, {
    name: 'updateProfileMutation'
  }),
  withUser
)(Profile);
